// @flow

import * as React from 'react';

import { Link } from 'react-router-dom';

import { selectCurrentId } from '@gandi/redux-sharing';
import { useTranslator, mapTags } from '@gandi/translate';

import { Alert, Spinner, Text } from '@gandi/ui';

import { withRouter } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { Intro } from '../Layout/components/Intro/Intro';
import { Layout } from '../components/Layout';
import NotFound from '../components/NotFound';
import { useResource } from '../hooks/resource';
import { handleMarketingParams } from '../modules/app/actions';
import type { StaticHandlerArgsWithMarketing } from '../modules/app/actions';
import type {
  PreFetchArgs,
  PreFetchReturn,
  WithRouterProps,
  FullState,
} from '../types';

import { CertificateForm } from './components/CertificateForm';
import { StepCSR } from './components/StepCSR';
import { StepType } from './components/StepType';
import { StepValidation } from './components/StepValidation';
import { Wizard, Step } from './components/Wizard';
import { STEP_CSR_FIELDS, STEP_TYPE_FIELDS } from './consts';
import {
  mapFormValuesMigrateToProductData,
  mapFormValuesRenewToProductData,
  renewableCertificateToFormValues,
} from './form/mapper';
import {
  preFetchCertificatesPrices,
  useCertificatePrices,
} from './hooks/prices';
import {
  certificateResource,
  selectCertificate,
  selectCertificateCreatePossibilities,
  selectCertificateRenewPossibilities,
} from './modules/certificate';

import type { FormValues } from './types';

//---
//  Notes for sectigo certificates, we allow to create a new certificate with digicert:
//  - digicert is selected by default
//  - when migrating from sectigo, the whole form is editable
//  - when creating the digicert cert we use `mapFormValuesMigrateToProductData` and pass the uuid from previous cert
//---

type Params = {
  certUUID: string,
};
type Props = WithRouterProps<Params>;

function CertificateConcrete({ params }: Props): React.Node {
  const { __ } = useTranslator();
  const { certUUID } = params;
  const sharingId = useAppSelector(selectCurrentId);
  const { url, label } = certificateResource.options.getConfigOne(
    certUUID,
    sharingId,
  );
  const { isLoaded, isFailure, isFailure403, isFailure404 } = useResource(
    certificateResource,
    url,
    label,
  );
  const certificate = useAppSelector(state =>
    selectCertificate(state, certUUID),
  );

  // Rq: we can detect `certificate.renewable === true` and eventually suggest a call to action e.g. «create a new cert» or «display cert»

  const initialValues = certificate
    ? {
        ...renewableCertificateToFormValues(certificate),
        // By default we force the new provider:
        provider: 'digicert',
      }
    : undefined;

  // Load prices used in the wizard
  useCertificatePrices({ process: 'renew' });

  const certificateRenewPossibilities = useAppSelector(
    selectCertificateRenewPossibilities,
  );

  const certificateCreatePossibilities = useAppSelector(
    selectCertificateCreatePossibilities,
  );

  const formDataMapper = (formValues: FormValues) => {
    if (!certificate) {
      return undefined;
    }

    if (certificate.provider.toLocaleLowerCase() !== formValues.provider) {
      return mapFormValuesMigrateToProductData(
        formValues,
        certificateCreatePossibilities,
      );
    }

    return mapFormValuesRenewToProductData(
      formValues,
      certificate,
      certificateRenewPossibilities,
    );
  };

  const title = certificate
    ? __('Renew SSL certificate: %(cn)s', {
        // sometimes IDNA is used in CSR so we force using utf-8 for display only.
        cn: certificate.cn_ulabel,
      })
    : __('Renew SSL certificate');

  const isStored = certificate?.stored;
  // we do not allow to change dcv for stored certs (e.g. shs)
  const forceAutoDCV = isStored;

  if (!isLoaded && !isFailure) {
    return (
      <Layout>
        <Intro title={title} />
        <Spinner centered />
      </Layout>
    );
  }

  if (isFailure || !certificate) {
    if (isFailure404) {
      return <NotFound />;
    }

    const error = mapTags(
      isFailure403
        ? __(
            'Unfortunately you cannot renew this certificate. You can create a [[link:new one]].',
          )
        : __(
            'An error has occurred. You can try to reload the page or create a [[link:new certificate]].',
          ),

      ({ link: linkContent }) => ({
        link: <Link to="/certificate/create">{linkContent}</Link>,
      }),
    );

    return (
      <Layout>
        <Intro title={title} />
        <Alert message={error} intent="danger" />
      </Layout>
    );
  }

  // we want to allow to edit form when it's sectigo
  const isReadOnly = certificate?.provider === 'Digicert';
  const providerAlert =
    certificate?.provider !== 'Digicert' ? (
      <Alert
        title={__('Digicert - Gandi offers a new CA partner')}
        message={
          <>
            <p>
              {mapTags(
                __(
                  'Moving forward, while improving security and quality, Gandi brings in a new CA partner to manage your certificates. We invite you to [[bold: recreate]] your certificate via this authority, as the previous offer will no longer be available in a few months.',
                ),
                ({ bold }) => ({
                  bold: <Text weight="semibold">{bold}</Text>,
                }),
              )}
            </p>
            <p>
              {__(
                'For your information, all issued certificates will remain valid until their expiration date.',
              )}
            </p>
          </>
        }
        marginBottom="small"
        withBackground
      />
    ) : null;

  return (
    <Layout>
      <Intro title={title} />

      <CertificateForm
        formDataMapper={formDataMapper}
        initialValues={initialValues}
      >
        <Wizard>
          <Step stepFields={STEP_TYPE_FIELDS}>
            <StepType
              readOnly={isReadOnly}
              process="renew"
              providerAlert={providerAlert}
            />
          </Step>
          <Step stepFields={STEP_CSR_FIELDS}>
            <StepCSR
              certToRenew={certificate}
              isStored={isStored}
              formDataMapper={formDataMapper}
            />
          </Step>
          <Step>
            <StepValidation forceAutoDCV={forceAutoDCV} />
          </Step>
        </Wizard>
      </CertificateForm>
    </Layout>
  );
}

CertificateConcrete.handleRedirect = async (
  args: StaticHandlerArgsWithMarketing<>,
) => {
  await handleMarketingParams(args);
  await Layout.handleRedirect(args);
};

CertificateConcrete.preFetch = async (
  args: PreFetchArgs<{}, Params, empty, FullState>,
): PreFetchReturn<> => {
  const { params, ...restLayoutArgs } = args;
  // XXX: there is an issue (on the whole shop?) there is no grid fetch on client side.
  // only SSR (`Layout.preFetch` does it somewhere). So if the prefetch is
  // missing, the prices will be based on the A grid only.
  // $FlowFixMe[prop-missing] flow doesn't see that we have overide params
  await Layout.preFetch({
    ...restLayoutArgs,
    params: {},
  });

  const {
    dispatch,
    getState,
    params: { certUUID },
  } = args;
  const sharingId = selectCurrentId(getState());
  const { url, label } = certificateResource.options.getConfigOne(
    certUUID,
    sharingId,
  );

  return Promise.allSettled([
    // XXX: we should exclude params from given args since `preFetchCertificatesPrices` doesn't accept any
    // $FlowFixMe[prop-missing]
    preFetchCertificatesPrices({ ...args, process: 'renew' }),
    dispatch(certificateResource.fetchOne(url, { label })),
  ]);
};

export const RenewCertificate: React.ComponentType<{}> =
  withRouter(CertificateConcrete);
